<template>
  <div v-if="insights && insights.length > 0" class="insight-ticker">
    <div class="insight-ticker-track" :style="{ animationDuration: tickerDuration }">
      <div v-for="copy in 4" :key="copy" class="ticker-set">
        <div
          v-for="(ins, i) in insights"
          :key="copy + ':' + i"
          class="insight-chip"
          :class="`insight-chip--${chipType(ins)}`"
        >
          <i :class="chipIcon(ins)"></i>
          <span v-html="ins.message"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'insights-ticker',
  props: {
    insights: { type: Array, default: () => [] }
  },
  computed: {
    /**
     * Duração da animação proporcional ao número de chips —
     * mantém velocidade visual consistente independente da quantidade.
     */
    tickerDuration () {
      const count = this.insights.length || 1
      const seconds = Math.max(20, count * 6)
      return `${seconds}s`
    }
  },
  methods: {
    chipType (insight) {
      // Backend pode mandar `type` (positive/warning/danger/info) ou
      // a gente infere a partir do `kind`.
      if (insight.type) return insight.type
      const map = {
        top_tag: 'positive',
        top_operator: 'positive',
        peak_hour: 'info',
        best_attendance: 'positive',
        stale_tag: 'warning',
        denied_repeat: 'danger',
        velocity_spike: 'warning',
        atypical_peak: 'warning',
        active_day: 'info',
        dominant_segment: 'info'
      }
      return map[insight.kind] || 'info'
    },
    chipIcon (insight) {
      // Aceita `bx ...`/`bxs ...`/`fa ...` mas o padrão do /dashboard usa
      // boxicons com classe completa. Garantimos que tem prefixo `bx`.
      if (insight.icon) {
        if (insight.icon.startsWith('bx ') || insight.icon.startsWith('bxs ')) return insight.icon
        if (insight.icon.startsWith('bx-')) return 'bx ' + insight.icon
        return insight.icon
      }
      return 'bx bx-bulb'
    }
  }
}
</script>

<style lang="scss" scoped>
.insight-ticker {
  position: relative;
  overflow: hidden;
  margin-bottom: 18px;
}

/* Fade nas pontas pra mascarar o scroll loop */
.insight-ticker::before,
.insight-ticker::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 48px;
  z-index: 2;
  pointer-events: none;
}

.insight-ticker::before {
  left: 0;
  background: linear-gradient(to right, #f8f9fc 40%, transparent);
}

.insight-ticker::after {
  right: 0;
  background: linear-gradient(to left, #f8f9fc 40%, transparent);
}

.insight-ticker-track {
  display: flex;
  width: max-content;
  animation: ticker-scroll linear infinite;
}

.insight-ticker-track:hover {
  animation-play-state: paused;
}

.ticker-set {
  display: flex;
  gap: 8px;
  padding-right: 8px;
}

.insight-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid transparent;
  white-space: nowrap;

  i { font-size: 14px; flex-shrink: 0; }

  ::v-deep strong {
    font-weight: 700;
  }
}

.insight-chip--positive {
  background: rgba(52, 195, 143, 0.08);
  border-color: rgba(52, 195, 143, 0.2);
  color: #1e9e6d;

  i { color: #34c38f; }
  ::v-deep strong { color: #1a7752; }
}

.insight-chip--warning {
  background: rgba(241, 180, 76, 0.1);
  border-color: rgba(241, 180, 76, 0.25);
  color: #9a6f00;

  i { color: #f1b44c; }
  ::v-deep strong { color: #7d5a00; }
}

.insight-chip--danger {
  background: rgba(244, 106, 106, 0.08);
  border-color: rgba(244, 106, 106, 0.2);
  color: #c0392b;

  i { color: #f46a6a; }
  ::v-deep strong { color: #962f24; }
}

.insight-chip--info {
  background: rgba(85, 110, 230, 0.07);
  border-color: rgba(85, 110, 230, 0.18);
  color: #3a4fa3;

  i { color: #556ee6; }
  ::v-deep strong { color: #2c3e8a; }
}

@keyframes ticker-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-25%); } /* 4 cópias = -25% repete o set */
}
</style>
